// Overlays: mobile bottom sheets (area help, levels, reading options), the
// desktop adjustment flyout, the desktop level meaning modal, and the toast.
// All use Radix Dialog for focus trapping, Escape and scrim behaviour; the
// content wrappers re-apply the `hfp-report` class because Radix portals
// render outside the report subtree (the tokens live on that class), and take
// the reading-options variables as an inline `readingStyle` for the same
// reason — without it the class resets text size and line spacing to 1.
import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { LEVELS, CATEGORY_TAGS } from '../hfpDisplay';
import { Mark, LevelChip } from './levels';
import { PagerButton } from './chrome';
import { LegalNotice } from './chrome';
import {
  CAT_ICONS,
  radioRing,
  radioRingSelected,
  tickSelected,
  iconDoneSnackbar,
  iconClockWhite,
  iconAlertWhite,
  iconCloseWhite,
} from '../icons';

// ---------------------------------------------------------------------------
// Shared adjustment content (mobile sheet + desktop flyout)
// ---------------------------------------------------------------------------

function CategoryTag({ category, withIcon = false }) {
  const tag = CATEGORY_TAGS[category];
  if (!tag) return null;
  return (
    <span
      className="flex items-center gap-[6px] shrink-0 pl-[8px] pr-[10px] py-[4px] rounded-[var(--hfp-radius-8)]"
      style={{ background: `var(${tag.surfaceVar})` }}
    >
      {withIcon && CAT_ICONS[category] && (
        <img src={CAT_ICONS[category]} alt="" aria-hidden="true" width={16} height={16} />
      )}
      <span
        className={`hfp-label-14 whitespace-nowrap ${withIcon ? '!text-[12px]' : ''}`}
        style={{ color: `var(${tag.inkVar})` }}
      >
        {tag.label}
      </span>
    </span>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-[8px] w-full">
      <span className="hfp-body-16 w-[12px] shrink-0 text-center text-[color:var(--hfp-ink-mid)]">•</span>
      <p className="hfp-body-16 flex-1 min-w-0 max-w-[900px] text-[color:var(--hfp-ink-mid)]">{children}</p>
    </div>
  );
}

/** One reasonable adjustment: number, title, category tag, why it helps, how-to bullets. */
export function AdjustmentCard({ adjustment, desktop = false }) {
  return (
    <div
      className={`flex w-full flex-col gap-[12px] rounded-[var(--hfp-radius-16)] border border-solid bg-[var(--hfp-surface-card)] ${
        desktop
          ? 'px-[24px] py-[20px] border-[color:var(--hfp-line-border)]'
          : 'px-[16px] py-[20px] border-[color:var(--hfp-line-hair)]'
      }`}
      style={{ boxShadow: desktop ? 'var(--hfp-shadow-card-desktop)' : 'var(--hfp-shadow-card)' }}
    >
      <div className={`flex ${desktop ? 'items-center' : 'items-start'} gap-[12px] w-full`}>
        <span className="flex items-center justify-center size-[28px] shrink-0 rounded-[var(--hfp-radius-8)] bg-[var(--hfp-surface-head-plum)]">
          <span className="hfp-label-14 text-[color:var(--hfp-ink-heading)]">{adjustment.number}</span>
        </span>
        <h3 className="hfp-heading-18 flex-1 min-w-0">{adjustment.title}</h3>
        <CategoryTag category={adjustment.category} withIcon={desktop} />
      </div>
      {adjustment.body && (
        <p className="hfp-body-16 w-full max-w-[900px] text-[color:var(--hfp-ink-mid)]">{adjustment.body}</p>
      )}
      {adjustment.bullets.length > 0 && (
        <>
          <p className={`hfp-label-14 whitespace-nowrap ${desktop ? '!text-[12px] tracking-[0.72px] uppercase text-[color:var(--hfp-ink-faint)]' : 'text-[color:var(--hfp-ink-mid)]'}`}>
            How to try it
          </p>
          {adjustment.bullets.map((bullet) => (
            <Bullet key={bullet}>{bullet}</Bullet>
          ))}
        </>
      )}
    </div>
  );
}

/** What might change / Potential impact: title and bullets. */
export function ImpactList({ bullets, desktop = false }) {
  if (!bullets || bullets.length === 0) return null;
  return (
    <div
      className="flex w-full flex-col overflow-hidden rounded-[var(--hfp-radius-16)] border border-solid border-[color:var(--hfp-line-border)] bg-[var(--hfp-surface-card)]"
      style={{ boxShadow: desktop ? 'var(--hfp-shadow-card-desktop)' : 'var(--hfp-shadow-card)' }}
    >
      <div className={`flex items-center gap-[8px] w-full px-[20px] py-[12px] ${desktop ? 'bg-[var(--hfp-level-well-bg)]' : 'bg-[var(--hfp-surface-card)]'}`}>
        {desktop && <Mark level="well" size={16} />}
        <span className={desktop ? 'hfp-meta-strong-14 text-[color:var(--hfp-ink-strong)]' : 'hfp-sub-16 text-[color:var(--hfp-ink-heading)]'}>
          {desktop ? 'Potential impact' : 'What might change'}
        </span>
      </div>
      <div className="flex w-full flex-col gap-[10px] px-[16px] pt-[16px] pb-[20px]">
        {bullets.map((bullet) => (
          <Bullet key={bullet}>{bullet}</Bullet>
        ))}
      </div>
    </div>
  );
}

/** Tinted why-this-matters panel at the top of an area's help content. */
export function WhyPanel({ title, body, desktop = false }) {
  if (!body) return null;
  if (desktop) {
    return (
      <div className="flex w-full flex-col gap-[8px] px-[20px] py-[16px] rounded-[var(--hfp-radius-12)] border border-solid border-[color:var(--hfp-line-info-teal)] bg-[var(--hfp-surface-info-teal)]">
        <span className="hfp-meta-strong-14 text-[color:var(--hfp-ink-strong)] whitespace-nowrap">{title}</span>
        <p className="hfp-body-16 w-full max-w-[900px] text-[color:var(--hfp-ink-mid)]">{body}</p>
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col gap-[8px] p-[16px] rounded-[var(--hfp-radius-16)] bg-[var(--hfp-surface-head-plum)]">
      <span className="hfp-sub-16 text-[color:var(--hfp-ink-heading)]">{title}</span>
      <p className="hfp-body-16 w-full max-w-[900px] text-[color:var(--hfp-ink-mid)]">{body}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile bottom sheet shell
// ---------------------------------------------------------------------------

/**
 * Bottom sheet: grabber + title header, scrolling body, and a close bar —
 * one outline Close pinned at the foot, plus tap outside. Never a filled
 * button inside a sheet.
 */
export function Sheet({ open, onOpenChange, title, children, tall = false, readingStyle }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-transparent" />
        <Dialog.Content
          className={`hfp-report hfp-sheet-anim fixed inset-x-0 bottom-0 z-50 flex flex-col overflow-hidden rounded-t-[var(--hfp-radius-20)] bg-[var(--hfp-ground-page)] ${
            tall ? 'top-[96px]' : 'max-h-[80vh]'
          }`}
          style={{ boxShadow: 'var(--hfp-shadow-sheet)', ...readingStyle }}
          aria-describedby={undefined}
        >
          <div className="relative flex flex-col shrink-0 pt-[8px] bg-[var(--hfp-surface-card)]">
            <span aria-hidden="true" className="absolute left-1/2 top-[8px] h-[5px] w-[36px] -translate-x-1/2 rounded-[3px] bg-[var(--hfp-line-hair)]" />
            <div className="flex items-center gap-[8px] pl-[16px] pr-[8px] pt-[12px] pb-[8px]">
              <Dialog.Title asChild>
                <h2 className="hfp-heading-18 flex-1 min-w-0">{title}</h2>
              </Dialog.Title>
            </div>
            <div aria-hidden="true" className="h-px w-full bg-[var(--hfp-line-hair)]" />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-[16px] pt-[28px] pb-[24px]">{children}</div>
          <div className="flex shrink-0 items-start px-[16px] pt-[8px] pb-[max(12px,env(safe-area-inset-bottom))] bg-[var(--hfp-surface-card)]">
            <Dialog.Close asChild>
              <button
                type="button"
                className="flex h-[48px] flex-1 items-center justify-center rounded-[var(--hfp-radius-12)] border-2 border-solid border-[color:var(--hfp-ink-heading)]"
              >
                <span className="hfp-sub-16 text-[color:var(--hfp-ink-heading)]">Close</span>
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The body of an area's help sheet: why panel, adjustments, impact. */
export function AreaHelpContent({ group, setting, childName }) {
  return (
    <div className="flex flex-col gap-[16px] w-full">
      <WhyPanel title={`Why this matters at ${setting}`} body={group.whyMatters} />
      <h3 className="hfp-heading-20 w-full">What could help {childName}</h3>
      {group.adjustments.map((adjustment) => (
        <AdjustmentCard key={adjustment.number} adjustment={adjustment} />
      ))}
      <ImpactList bullets={group.impactBullets} />
    </div>
  );
}

/** Body of the "What the levels mean" sheet. */
export function LevelsSheetContent() {
  return (
    <div className="flex flex-col gap-[12px] w-full px-[16px] pb-[8px]">
      <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-strong)]">
        Every area has one level from home and one from school. They are never added up or
        averaged. Each level is a word, a shape and a colour, so it never depends on colour alone.
      </p>
      {['priority', 'support', 'well', 'none'].map((level) => (
        <div key={level} className="flex flex-col items-start gap-[6px] py-[8px] w-full">
          <LevelChip level={level} />
          <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-strong)]">{LEVELS[level].meaning}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reading options (Aa)
// ---------------------------------------------------------------------------

const TEXT_SIZE_OPTIONS = [
  { value: 1, label: 'Standard', sub: 'Your phone’s own size' },
  { value: 1.25, label: 'Larger', sub: 'A quarter bigger' },
  { value: 1.5, label: 'Largest', sub: 'Half as big again' },
];
const LINE_SPACING_OPTIONS = [
  { value: 1, label: 'Standard', sub: '' },
  { value: 1.35, label: 'More', sub: 'More space between lines' },
];

function RadioRow({ option, checked, onSelect, name }) {
  return (
    <label className={`flex h-[48px] w-full cursor-pointer items-center gap-[12px] px-[12px] rounded-[10px] ${checked ? 'bg-[var(--hfp-surface-head-plum)]' : ''}`}>
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={() => onSelect(option.value)}
        className="sr-only"
      />
      <img src={checked ? radioRingSelected : radioRing} alt="" aria-hidden="true" width={22} height={22} />
      <span className="flex flex-1 min-w-0 flex-col">
        <span className={`${checked ? 'hfp-body-strong-16' : 'hfp-body-16'} !text-[16px] !leading-[24px] text-[color:var(--hfp-ink-strong)] whitespace-nowrap`}>
          {option.label}
        </span>
        {option.sub && <span className="hfp-meta-14 text-[color:var(--hfp-ink-mid)] whitespace-nowrap">{option.sub}</span>}
      </span>
      {checked && (
        <span className="flex items-center gap-[6px] shrink-0">
          <img src={tickSelected} alt="" aria-hidden="true" width={16} height={16} />
          <span className="hfp-meta-14 text-[color:var(--hfp-ink-heading)] whitespace-nowrap">Selected</span>
        </span>
      )}
    </label>
  );
}

function RadioGroupCard({ title, name, options, value, onSelect }) {
  return (
    <div className="flex flex-col gap-[8px] w-full" role="radiogroup" aria-label={title}>
      <span className="hfp-body-strong-16 !text-[16px] text-[color:var(--hfp-ink-strong)] whitespace-nowrap">{title}</span>
      <div className="flex w-full flex-col overflow-hidden rounded-[var(--hfp-radius-12)] border border-solid border-[color:var(--hfp-line-hair)] bg-[var(--hfp-surface-card)]">
        {options.map((option, i) => (
          <React.Fragment key={option.label}>
            {i > 0 && <span aria-hidden="true" className="h-px w-full bg-[var(--hfp-line-hair)]" />}
            <RadioRow option={option} name={name} checked={value === option.value} onSelect={onSelect} />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/** Body of the Reading options sheet. Changes apply as you tap. */
export function ReadingOptionsContent({ specimen, textScale, lineSpacing, onChange }) {
  return (
    <div className="flex flex-col gap-[16px] w-full px-[16px] pb-[8px]">
      <div className="flex w-full flex-col gap-[4px] p-[12px] rounded-[var(--hfp-radius-12)] bg-[var(--hfp-surface-head-plum)]">
        <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-strong)]">{specimen}</p>
      </div>
      <RadioGroupCard
        title="Text size"
        name="hfp-text-size"
        options={TEXT_SIZE_OPTIONS}
        value={textScale}
        onSelect={(value) => onChange({ textScale: value })}
      />
      <RadioGroupCard
        title="Line spacing"
        name="hfp-line-spacing"
        options={LINE_SPACING_OPTIONS}
        value={lineSpacing}
        onSelect={(value) => onChange({ lineSpacing: value })}
      />
      <p className="hfp-meta-14 !text-[16px] !leading-[24px] hfp-body-16 w-full text-[color:var(--hfp-ink-mid)]">
        These sit on top of your phone’s own text size. Your choices stay on this phone and apply
        to the whole report. They change as you tap, so you can see them behind this panel.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Desktop: meaning modal + adjustment flyout
// ---------------------------------------------------------------------------

// The meaning modal carries its own ratified short meanings (the "meaning
// modals · one per mark" card), distinct from the levels-sheet wording.
const MODAL_MEANINGS = {
  priority: 'Where focused support would help most. Where to start, not something wrong.',
  support: 'Some support or small changes would help.',
  well: 'Worth keeping whatever makes it work.',
  none: 'No answer here yet. A gap, never a judgement.',
};

const NOT_ASKED_BODY =
  'This side was not asked about this area, so there is nothing to compare here.';
const NOT_ASKED_DETAIL =
  'Home and school answered the same questions separately, without seeing each other’s answers. ' +
  'A gap on one side is not a disagreement; it is simply a question that was not part of that setting’s form.';
const NOT_ANSWERED_BODY = 'This has not been answered yet. It can still be filled in.';
const WELL_DETAIL_1 =
  'This area is going well. Celebrate it and consider how these strengths can support other areas.';
const WELL_DETAIL_2 =
  'No adjustment is suggested for an area that is going well. Where home and school differ, the ' +
  'difference is information about the two places, never a contest about who is right.';

/**
 * Meaning modal: opens centred over the page from any underlined level word
 * and answers in place — what the mark means, then what could help for that
 * area and setting. Going well and Nothing recorded carry guidance instead.
 */
export function MeaningModal({ open, onOpenChange, area, setting, level, group, readingStyle }) {
  if (!area) return null;
  const info = LEVELS[level?.key] || LEVELS.none;
  const word = info.word;
  const meaningBody =
    level?.key === 'none'
      ? level?.notAsked
        ? NOT_ASKED_BODY.replace('This side', setting === 'home' ? 'Home' : 'School')
        : NOT_ANSWERED_BODY
      : MODAL_MEANINGS[level?.key] || info.meaning;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[var(--hfp-overlay-scrim)]" />
        <Dialog.Content
          className="hfp-report hfp-modal-anim fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-[760px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 flex-col gap-[12px] overflow-y-auto rounded-[var(--hfp-radius-8)] bg-[var(--hfp-surface-card)] px-[28px] py-[24px]"
          style={{ boxShadow: 'var(--hfp-shadow-modal)', ...readingStyle }}
          aria-describedby={undefined}
        >
          <div className="flex items-start gap-[12px] w-full">
            <Mark level={level?.key || 'none'} size={24} />
            <div className="flex flex-1 min-w-0 flex-col gap-[2px]">
              <Dialog.Title asChild>
                <h2 className="hfp-body-strong-16 !text-[20px] text-[color:var(--hfp-ink-strong)]">{word}</h2>
              </Dialog.Title>
              <span className="hfp-meta-14 text-[color:var(--hfp-ink-soft)]">
                {area.name} · at {setting}
              </span>
            </div>
            <Dialog.Close asChild>
              <button type="button" aria-label="Close" className="flex size-[44px] items-center justify-center rounded-[var(--hfp-radius-8)]">
                <span className="hfp-body-16 text-[color:var(--hfp-ink-mid)]" aria-hidden="true">✕</span>
              </button>
            </Dialog.Close>
          </div>
          <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-mid)]">{meaningBody}</p>
          <span aria-hidden="true" className="h-px w-full bg-[var(--hfp-line-hair)]" />
          {level?.key === 'well' && (
            <>
              <h3 className="hfp-sub-16 text-[color:var(--hfp-ink-heading)]">What this means</h3>
              <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-mid)]">{WELL_DETAIL_1}</p>
              <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-mid)]">{WELL_DETAIL_2}</p>
            </>
          )}
          {level?.key === 'none' && (
            <>
              <h3 className="hfp-sub-16 text-[color:var(--hfp-ink-heading)]">
                {level?.notAsked ? 'Why there is no answer' : 'What this means'}
              </h3>
              <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-mid)]">
                {level?.notAsked ? NOT_ASKED_DETAIL : 'No answer here yet. A gap to fill, never a judgement.'}
              </p>
            </>
          )}
          {(level?.key === 'priority' || level?.key === 'support') && group && (
            <>
              <h3 className="hfp-sub-16 text-[color:var(--hfp-ink-heading)]">What could help at {setting}</h3>
              {group.adjustments.map((adjustment) => (
                <div key={adjustment.number} className="flex items-start gap-[12px] w-full">
                  <span className="flex items-center justify-center size-[24px] shrink-0 rounded-[6px] bg-[var(--hfp-surface-head-plum)]">
                    <span className="hfp-label-14 !text-[12px] text-[color:var(--hfp-ink-heading)]">{adjustment.number}</span>
                  </span>
                  <span className="flex flex-1 min-w-0 flex-col gap-[2px]">
                    <span className="flex items-center gap-[10px] w-full">
                      <span className="hfp-body-strong-16 text-[color:var(--hfp-ink-strong)]">{adjustment.title}</span>
                      <CategoryTag category={adjustment.category} withIcon />
                    </span>
                    {adjustment.body && (
                      <span className="hfp-meta-14 !text-[15px] !leading-[22px] text-[color:var(--hfp-ink-mid)]">{adjustment.body}</span>
                    )}
                  </span>
                </div>
              ))}
              {group.impactBullets.length > 0 && (
                <>
                  <span aria-hidden="true" className="h-px w-full bg-[var(--hfp-line-hair)]" />
                  <span className="flex items-center gap-[8px]">
                    <Mark level="well" size={16} />
                    <span className="hfp-meta-strong-14 text-[color:var(--hfp-ink-strong)]">Potential impact</span>
                  </span>
                  {group.impactBullets.map((bullet) => (
                    <Bullet key={bullet}>{bullet}</Bullet>
                  ))}
                </>
              )}
            </>
          )}
          <div className="flex w-full items-start justify-end pt-[4px]">
            <Dialog.Close asChild>
              <button
                type="button"
                className="flex items-center px-[16px] py-[10px] rounded-[var(--hfp-radius-8)] border border-solid border-[color:var(--hfp-line-hair)] bg-[var(--hfp-surface-card)]"
              >
                <span className="hfp-label-14 text-[color:var(--hfp-ink-mid)]">Close</span>
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Desktop adjustment flyout: right-hand panel over a scrim, with head
 * (title, sub, level chip, ✕), scrolling body and a prev/next foot cycling
 * through the hub's areas.
 */
export function DesktopFlyout({ open, onOpenChange, group, setting, childName, onPrevious, onNext, previousLabel, nextLabel, readingStyle, calibrationNote }) {
  if (!group) return null;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[var(--hfp-overlay-scrim)]" />
        <Dialog.Content
          className="hfp-report hfp-modal-anim fixed right-0 top-0 z-50 flex h-full w-[860px] max-w-[92vw] flex-col overflow-hidden rounded-l-[16px] bg-[var(--hfp-surface-card)]"
          style={{ boxShadow: 'var(--hfp-shadow-flyout)', ...readingStyle }}
          aria-describedby={undefined}
        >
          <div className="flex items-start gap-[12px] w-full pl-[24px] pr-[16px] pt-[20px] pb-[16px] bg-[var(--hfp-surface-card)]">
            <div className="flex flex-1 min-w-0 flex-col gap-[6px]">
              <Dialog.Title asChild>
                <h2 className="hfp-heading-20 whitespace-nowrap">Adjustments for {group.areaName.charAt(0).toLowerCase() + group.areaName.slice(1)}</h2>
              </Dialog.Title>
              <p className="hfp-meta-14 w-full text-[color:var(--hfp-ink-soft)]">
                What could be done at {setting} to help {childName} with {group.areaName.toLowerCase()}
              </p>
              <LevelChip level={group.level} />
            </div>
            <Dialog.Close asChild>
              <button type="button" aria-label="Close" className="flex size-[44px] items-center justify-center rounded-[var(--hfp-radius-8)]">
                <span className="hfp-body-16 !text-[18px] text-[color:var(--hfp-ink-mid)]" aria-hidden="true">✕</span>
              </button>
            </Dialog.Close>
          </div>
          <span aria-hidden="true" className="h-px w-full bg-[var(--hfp-line-hair)]" />
          <div className="flex flex-1 min-h-0 flex-col gap-[16px] overflow-y-auto px-[24px] pt-[20px] pb-[24px] bg-[var(--hfp-surface-card)]">
            <WhyPanel desktop title="Why this matters" body={group.whyMatters} />
            <h3 className="hfp-heading-20 w-full">What may help {childName}</h3>
            {group.adjustments.map((adjustment) => (
              <AdjustmentCard key={adjustment.number} adjustment={adjustment} desktop />
            ))}
            <ImpactList bullets={group.impactBullets} desktop />
            <LegalNotice calibrationNote={calibrationNote} />
          </div>
          <span aria-hidden="true" className="h-px w-full bg-[var(--hfp-line-hair)]" />
          <div className="flex h-[72px] shrink-0 items-center gap-[12px] px-[24px] py-[14px] bg-[var(--hfp-surface-card)]">
            <PagerButton kind="previous" label={previousLabel ? `‹ ${previousLabel}` : '‹ Previous'} onClick={onPrevious} disabled={!previousLabel} />
            <div className="flex-1" />
            <PagerButton kind="next" label={nextLabel ? `Next: ${nextLabel} ›` : 'Next ›'} onClick={onNext} disabled={!nextLabel} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---------------------------------------------------------------------------
// Toast (PDF download states). Ready closes on the 44 target or after 6s;
// Preparing has no close and clears itself; Failed keeps the close.
// ---------------------------------------------------------------------------
const TOAST_ICONS = { ready: iconDoneSnackbar, preparing: iconClockWhite, failed: iconAlertWhite };

export function Snackbar({ kind, message, detail, onClose }) {
  return (
    <div
      role="status"
      className="hfp-report flex w-full items-start gap-[12px] rounded-[var(--hfp-radius-14)] bg-[var(--hfp-ink-heading)] pl-[16px] pr-[12px] py-[14px]"
      style={{ boxShadow: 'var(--hfp-shadow-toast)' }}
    >
      <img src={TOAST_ICONS[kind]} alt="" aria-hidden="true" width={22} height={22} className="mt-[1px]" />
      <div className="flex flex-1 min-w-0 flex-col gap-[2px]">
        <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-on-plum)]">{message}</p>
        {detail && <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-on-plum)]">{detail}</p>}
      </div>
      {kind !== 'preparing' && (
        <button type="button" aria-label="Close" onClick={onClose} className="flex size-[44px] shrink-0 items-center justify-center rounded-[22px]">
          <img src={iconCloseWhite} alt="" aria-hidden="true" width={16} height={16} />
        </button>
      )}
    </div>
  );
}
